/**
 * This is a reimplementation of Backbone.Events from scratch which passes the
 * Backbone.Events unit tests (minus the tests which are used to track internal
 * data representation)
 */
const events = {};

/**
 * Wraps a function in a wrapper function which will only ever run the function
 * once and will call an optional after function when it has ran.
 *
 * @param  {Function} fn
 * @param  {?Function=} after
 * @return {Function}
 */
const once = function(fn, after) {
  let hasRun = false;
  return function(...args) {
    if (hasRun) {
      return;
    }
    hasRun = true;
    fn.apply(this, args);
    if (!!after) {
      after();
    }
  };
};

/**
 * Sets the listeners (listenTo, listenToOnce) for an object
 * @param {Object} obj
 * @param {Array.<Object>} listeners
 */
const setListeners = function(obj, listeners) {
  obj.__listeners = listeners;
};

/**
 * Retrieves the list of listeners (listenTo, listenToOnce) for an object and
 * initializes them if specified
 *
 * @param  {Object} obj
 * @param  {boolean=} shouldInitIfUndefined
 * @return {Array.<Object>}
 */
const getListeners = function(obj, shouldInitIfUndefined) {
  if (!obj.__listeners && shouldInitIfUndefined) {
    setListeners(obj, []);
  }
  return obj.__listeners;
};

/**
 * Retrieves the list of handlers (on, once) for an object and initializes
 * them if specified
 *
 * @param  {Object} obj
 * @param  {boolean=} shouldInitIfUndefined
 * @return {Object.<string, Array.<Object>>}
 */
const getHandlers = function(obj, shouldInitIfUndefined) {
  if (!obj.__handlers && shouldInitIfUndefined) {
    obj.__handlers = {};
  }
  return obj.__handlers;
};

/**
 * Takes in event, callback, and context in supported Backbone syntaxes and
 * generates a normalized array of events with their callbacks and contexts
 *
 * @param  {string|Object.<string, Function>=}   event
 * @param  {Function=} callback [description]
 * @param  {Object=}   context  [description]
 * @return {Object}            [description]
 */
const normalizeEvents = function(event, callback, context) {
  let events = [],
    eventNames,
    i;

  if (typeof event === 'string') {
    // handle space delimited event strings
    eventNames = event.split(' ');
    for (i = 0; i < eventNames.length; i += 1) {
      events.push({
        name: eventNames[i],
        callback,
        originalCallback: callback,
        context,
      });
    }
  } else if (typeof event === 'object' && !!event) {
    // handle mapping of strings to handler functions
    Object.keys(event).forEach((eventName) => {
      events = events.concat(
        normalizeEvents(eventName, event[eventName], !!context ? context : callback),
      );
    });
  }

  return events;
};

/**
 * Normalizes input events and runs a function against each event
 *
 * @param  {string|Object.<string,Function>=}   events
 * @param  {?Function} callback
 * @param  {?Object}   context
 * @param  {Function} fn
 */
const forEachEvent = function(events, callback, context, fn) {
  const e = normalizeEvents(events, callback, context);
  for (let i = 0; i < e.length; i += 1) {
    fn(e[i]);
  }
};

/**
 * Attaches a listener to an object for a particular event
 *
 * @param  {Object} obj
 * @param  {Object} event
 */
const _on = function(obj, event) {
  const handlers = getHandlers(obj, true);

  if (typeof handlers[event.name] === 'undefined') {
    handlers[event.name] = [event];
  } else {
    handlers[event.name].push(event);
  }
};

/**
 * Subscribes an object to events emitted by another object
 *
 * @param  {Object} obj
 * @param  {Object} otherObj
 * @param  {Array.<Object>} events
 */
const _listenTo = function(obj, otherObj, events) {
  const listeners = getListeners(obj, true);

  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];
    if (!event.callback) {
      return;
    }

    _on(otherObj, event);
    listeners.push({
      obj: otherObj,
      event,
      originalCallback: event.originalCallback,
    });
  }
};

/**
 * Subscribe to events on an object
 *
 * @param  {string|Object.<string,Function>}   events
 * @param  {Function|Object=} callback
 * @param  {Object=}   context
 * @return {Object}
 */
events.on = function(events, callback, context) {
  forEachEvent(events, callback, context, (event) => {
    if (!event.callback) {
      return;
    }

    _on(this, event);
  });

  return this;
};

/**
 * Unscribe from events on an object
 *
 * @param  {string|Object.<string,Function>}   events
 * @param  {Function|Object=} callback
 * @param  {Object=}   context
 * @return {Object}
 */
events.off = function(events, callback, context) {
  const handlers = getHandlers(this, false);
  if (typeof handlers === 'undefined') {
    return this;
  }

  const handlerKeys = Object.keys(handlers);
  let normalizedEvents = normalizeEvents(events, callback, context);
  if (normalizedEvents.length === 0) {
    normalizedEvents = [
      {
        name: null,
        callback,
        context,
      },
    ];
  }

  for (let k = 0; k < normalizedEvents.length; k += 1) {
    const event = normalizedEvents[k];
    let checkKeys;

    // find the set of event names to check
    if (!event.name) {
      checkKeys = handlerKeys;
    } else {
      checkKeys = [event.name];
    }

    for (let i = 0; i < checkKeys.length; i += 1) {
      const key = checkKeys[i];
      const keyHandlers = handlers[key];
      if (typeof keyHandlers !== 'undefined') {
        const newHandlers = [];
        for (let j = 0; j < keyHandlers.length; j += 1) {
          const handler = keyHandlers[j];
          if (
            !!event.callback &&
            handler.originalCallback !== event.callback &&
            handler.callback !== event.callback
          ) {
            newHandlers.push(handler);
          } else if (!!event.context && handler.context !== event.context) {
            newHandlers.push(handler);
          }
        }
        handlers[key] = newHandlers;
      }
    }
  }

  return this;
};

/**
 * Trigger events on an object
 *
 * @param  {string|Object.<string,Function>}   event
 * @param  {*} args
 * @return {Object}
 */
events.trigger = function(event, ...args) {
  const handlers = getHandlers(this, false);
  if (typeof handlers === 'undefined') {
    return this;
  }
  let i, j, h;
  const eventNames = event.split(' ');

  for (i = 0; i < eventNames.length; i += 1) {
    let pendingHandlers = [];
    const allHandlers = handlers.all;

    const eventName = eventNames[i];
    const eventHandlers = handlers[eventName];

    if (typeof eventHandlers !== 'undefined') {
      for (j = 0; j < eventHandlers.length; j += 1) {
        h = eventHandlers[j];
        if (typeof h.callback === 'function') {
          pendingHandlers.push({
            fn: h.callback,
            context: h.context || this,
            args,
          });
        } else if (!!h.callback) {
          throw new Error('Callbacks must be functions');
        }
      }
    }

    if (typeof allHandlers !== 'undefined') {
      for (j = 0; j < allHandlers.length; j += 1) {
        h = allHandlers[j];
        if (typeof h.callback === 'function') {
          pendingHandlers.push({
            fn: h.callback,
            context: h.context || this,
            args: [eventName].concat(args),
          });
        } else if (!!h.callback) {
          throw new Error('Callbacks must be functions');
        }
      }
    }

    for (j = 0; j < pendingHandlers.length; j += 1) {
      h = pendingHandlers[j];
      h.fn.apply(h.context, h.args);
    }

    pendingHandlers = [];
  }

  return this;
};

/**
 * Subscribe to events on an object but only trigger the callbacks once per event
 *
 * @param  {string|Object.<string,Function>}   events
 * @param  {Function|Object=} callback
 * @param  {Object=}   context
 * @return {Object}
 */
events.once = function(events, callback, context) {
  forEachEvent(events, callback, context, (event) => {
    if (typeof event.originalCallback !== 'function') {
      return;
    }

    event.callback = once(event.originalCallback, () => {
      this.off(event.name, event.callback, event.context);
    });
    _on(this, event);
  });

  return this;
};

/**
 * Subscribe to events on another object
 *
 * @param  {Object}   otherObj
 * @param  {Object}   events
 * @param  {Function} callback
 * @return {Object}
 */
events.listenTo = function(otherObj, events, callback) {
  _listenTo(this, otherObj, normalizeEvents(events, callback, this));
  return this;
};

/**
 * Subscribe to events on another object but only run the callbacks once per
 * event
 *
 * @param  {Object}   otherObj
 * @param  {Object}   events
 * @param  {Function} callback
 * @return {Object}
 */
events.listenToOnce = function(otherObj, events, callback) {
  const normalizedEvents = normalizeEvents(events, callback, this);
  const onceEvents = normalizedEvents.map((event) => {
    if (event.originalCallback) {
      event.callback = once(event.originalCallback, () => {
        this.stopListening(otherObj, event.name, event.originalCallback, event.context);
      });
    }
    return event;
  });
  _listenTo(this, otherObj, onceEvents);
  return this;
};

/**
 * Unsubscribe to events from another object
 *
 * @param  {Object}   obj
 * @param  {Object}   event
 * @param  {Function} callback
 * @return {Object}
 */
events.stopListening = function(obj, event, callback) {
  const listeners = getListeners(this, false);
  let listener;
  if (!listeners) {
    return this;
  }

  let i, j;
  const newListeners = [];

  const events = typeof event === 'undefined' ? undefined : normalizeEvents(event, callback, this);

  for (i = 0; i < listeners.length; i += 1) {
    listener = listeners[i];

    if (listener.obj !== obj && !!obj) {
      // removing listeners for a different obj
      newListeners.push(listener);
    } else if (!!event) {
      let shouldRemove = false;
      for (j = 0; j < events.length && shouldRemove === false; j += 1) {
        const e = events[j];
        if (listener.event.name === e.name && listener.originalCallback === e.callback) {
          shouldRemove = true;
        }
      }
      if (!shouldRemove) {
        newListeners.push(listener);
      } else {
        listener.obj.off(listener.event.name, listener.event.callback, listener.event.context);
      }
    } else {
      listener.obj.off(listener.event.name, listener.event.callback, listener.event.context);
    }
  }

  setListeners(this, newListeners);
  return this;
};

module.exports = events;
